import { computed } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'

import {
  PosthogFeatureFlags,
  usePosthogFlag,
  usePosthogFlagsSettled,
} from '@/lib/posthog'
import { queryClient } from '@/lib/queryClient'
import { useToast } from '@/store/toast'

import { handleApiErrorToast } from '..'
import { AnnouncementsApi } from '../modules/announcements'

import type {
  IAnnouncement,
  IAnnouncementContent,
  IAnnouncementUpdate,
} from '../types/announcements'

export const announcementQueryKeys = {
  all: ['announcements'] as const,
  admin: () => [...announcementQueryKeys.all, 'admin'] as const,
  /**
   * Prefix covering every user's active list. Invalidate against this — vue-query
   * matches query keys by prefix, so one call reaches all of them.
   */
  activeRoot: () => [...announcementQueryKeys.all, 'active'] as const,
  /**
   * Scoped per user: the cache outlives a logout (nothing clears it, and Keycloak
   * logout is a same-tab redirect), so an unscoped key would hand the next user
   * the list fetched for the previous one. Only a delayed announcement while
   * announcements go to everyone, but it becomes a genuine leak the moment any
   * form of audience targeting exists.
   */
  active: (userId: string | null | undefined) =>
    [...announcementQueryKeys.activeRoot(), userId ?? 'anonymous'] as const,
}

export const useAnnouncementQueries = () => {
  const isAnnouncementsEnabled = usePosthogFlag(
    PosthogFeatureFlags.Announcements,
  )
  const haveFeatureFlagsSettled = usePosthogFlagsSettled()

  /**
   * `handleApiErrorToast` toasts and then re-rejects, so a failed load settles
   * the query as `error` with no data rather than resolving to an empty list.
   * That distinction is the caller's to render — an admin shown "no
   * announcements" after a 500 would create a duplicate of something live.
   *
   * Held until PostHog has reported flags, and skipped entirely when the
   * announcements flag is off, so mounting this query cannot call
   * `GET /admin/announcements` on the fail-open default.
   */
  const adminList = () =>
    useQuery<IAnnouncement[]>({
      queryKey: announcementQueryKeys.admin(),
      queryFn: () => AnnouncementsApi.list().catch(handleApiErrorToast),
      enabled: computed(
        () => haveFeatureFlagsSettled.value && isAnnouncementsEnabled.value,
      ),
    })

  const invalidateAdminList = () =>
    queryClient.invalidateQueries({ queryKey: announcementQueryKeys.admin() })

  /**
   * Any edit changes what every user sees — there is no revision snapshot to
   * hold it back — so the end-user cache has to go too. Otherwise an admin
   * previewing the app in another tab keeps the stale `active` payload until
   * its staleTime lapses.
   */
  const invalidateAll = () => {
    invalidateAdminList()
    queryClient.invalidateQueries({
      queryKey: announcementQueryKeys.activeRoot(),
    })
  }

  const createAnnouncement = useMutation<IAnnouncement, Error, string>({
    mutationFn: (name) => AnnouncementsApi.create(name),
    onSuccess: invalidateAdminList,
    onError: () => useToast().onError('Could not create the announcement'),
  })

  const updateAnnouncement = useMutation<
    IAnnouncement,
    Error,
    { id: string; body: IAnnouncementUpdate }
  >({
    mutationFn: ({ id, body }) => AnnouncementsApi.update(id, body),
    onSuccess: invalidateAll,
    onError: () => useToast().onError('Could not save the announcement'),
  })

  const deleteAnnouncement = useMutation<void, Error, string>({
    mutationFn: (id) => AnnouncementsApi.remove(id),
    onSuccess: () => {
      invalidateAll()
      useToast().onSuccess('Announcement deleted')
    },
    onError: () => useToast().onError('Could not delete the announcement'),
  })

  /**
   * `content` is what the server already holds — the caller flushes pending
   * edits first — and is what sizes the display window server-side.
   */
  const publishAnnouncement = useMutation<
    IAnnouncement,
    Error,
    { id: string; content: IAnnouncementContent; isCurrentlyActive?: boolean }
  >({
    mutationFn: ({ id, content, isCurrentlyActive }) =>
      AnnouncementsApi.publish(id, content, isCurrentlyActive),
    onSuccess: invalidateAll,
    onError: () => useToast().onError('Could not publish the announcement'),
  })

  const unpublishAnnouncement = useMutation<IAnnouncement, Error, string>({
    mutationFn: (id) => AnnouncementsApi.unpublish(id),
    onSuccess: () => {
      invalidateAll()
      useToast().onSuccess('Announcement unpublished')
    },
    onError: () => useToast().onError('Could not unpublish the announcement'),
  })

  return {
    adminList,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    publishAnnouncement,
    unpublishAnnouncement,
  }
}
